import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AccountWorkspaceActionsContext } from "../auth/AccountWorkspaceActions";
import { useClinicalRuntime } from "../auth/ClinicalRuntimeProvider";
import {
  draftsHandoutEveryVisit,
  useWorkforceAccount,
  workforceInitials,
  workforceRoleLabel,
} from "../lib/workforceAccount";
import {
  allowedLandingPages,
  applyWorkforcePreferences,
} from "../pages/account/PreferencesSettings";
import "../appearance.css";
import { useDemoScheduling } from "../demo/DemoSchedulingProvider";
import { useDemoWorkspace } from "../demo/DemoWorkspaceProvider";
import {
  clearNoteDraftSync,
  flushNoteDrafts,
  hasUnsavedNoteDrafts,
} from "../demo/noteWorkingDraft";
import "../horizon-encounter.css";
import "../ice-blue-shell.css";
import "../soft-glass-shell.css";
import { clearClinicalSession } from "../lib/clinicalSession";
import { useWorkforceProfile } from "../lib/useWorkforceProfile";
import { visitDateLabel } from "../pages/patient-profile/visitFormat";
import { useEncounterSelection } from "../state/encounterSelection";
import { RecordingScopeContext, useMicrophoneOwner } from "../state/microphoneOwnership";
import { type PatientProfileTab, usePatientProfileTab } from "../state/patientProfileTab";
import { useSignedNoteTabs } from "../state/signedNoteTabs";
import { clearTodayBoardView } from "../state/todayBoardView";
import "./bob-brand.css";
import { ClinicMessagingProvider } from "../messaging/ClinicMessaging";
import { AttentionBadge } from "./AttentionBadge";
import { Bob } from "./Bob";
import { CommandPalette } from "./CommandPalette";
import { EncounterSwitchConfirm } from "./EncounterSwitchConfirm";
import { Icon, type IconName } from "./Icon";
import { IncomingDocumentAlerts } from "./IncomingDocumentAlerts";
import { RouteLoading } from "./RouteLoading";
import { SidebarAccountMenu } from "./SidebarAccountMenu";

// The shell owns the Ice Blue encounter surface (keep-alive tab pane below);
// same lazy chunk the router splits for the persisted route family.
// The persisted surface is kept alive by the same pane, so the shell mounts its
// provider stack directly rather than letting the route own (and unmount) it.
const PersistedHorizonEncounter = lazy(() => import("../pages/PersistedHorizonEncounter"));
/** How long logging out waits for open visits to send their waiting note changes. */
const NOTE_DRAFT_SIGN_OUT_FLUSH_MS = 4_000;
// Each patient profile lives in its own keep-alive pane, so the shell mounts
// it rather than the route.
const PatientProfilePage = lazy(() => import("../pages/PatientProfilePage"));

const navigation: Array<{
  to: "/" | "/schedule" | "/patients" | "/attention" | "/billing" | "/documents" | "/admin";
  label: string;
  icon: IconName;
  badge?: string;
}> = [
  { to: "/", label: "Today", icon: "calendar" },
  { to: "/schedule", label: "Schedule", icon: "clock" },
  { to: "/patients", label: "Patients", icon: "person" },
  { to: "/attention", label: "Attention", icon: "inbox" },
  { to: "/billing", label: "Billing", icon: "billing" },
  { to: "/admin", label: "Admin", icon: "settings" },
];

type SectionPath = (typeof navigation)[number]["to"];

/** Child workspaces belong to their sidebar section. */
function sectionForPath(pathname: string): SectionPath | null {
  if (pathname === "/" || pathname === "/encounter" || pathname.startsWith("/visit-note/")) {
    return "/";
  }
  const root = `/${pathname.split("/")[1] ?? ""}`;
  return navigation.find((item) => item.to === root)?.to ?? null;
}

interface HorizonShellMenu {
  expanded: boolean;
  open(trigger: HTMLElement | null): void;
}

const HorizonShellMenuContext = createContext<HorizonShellMenu>({
  expanded: false,
  open: () => undefined,
});

export function useHorizonShellMenu(): HorizonShellMenu {
  return useContext(HorizonShellMenuContext);
}

export function AppShell() {
  return (
    <ClinicMessagingProvider>
      <AppShellContent />
    </ClinicMessagingProvider>
  );
}

function AppShellContent() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [accountDirty, setAccountDirty] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const {
    clearEncounterSelection,
    selectedEncounter,
    encounterTabs,
    selectEncounter,
    closeEncounterTab: closeSelectionTab,
    microphoneBusy,
    pendingEncounterSwitch,
    confirmEncounterSwitch,
    cancelEncounterSwitch,
  } = useEncounterSelection();
  const microphone = useMicrophoneOwner();
  const runtime = useClinicalRuntime();
  const workforceProfile = useWorkforceProfile();
  const account = useWorkforceAccount();
  // The signed-in physician's "patient handout at every visit" setting (EXECDEV-251), for the
  // encounters this shell keeps open; off until the account loads.
  const handoutEveryVisit = draftsHandoutEveryVisit(account.data);
  const initialLandingApplied = useRef(false);
  const preferences = account.data?.preferences;
  const sidebarKey = account.data
    ? `emr.sidebar.${account.data.tenantId}.${account.data.userId}`
    : null;
  const location = useLocation();
  useEffect(() => {
    if (!preferences) return;
    applyWorkforcePreferences(preferences);
    let saved: string | null = null;
    try {
      saved = preferences.rememberSidebar && sidebarKey ? localStorage.getItem(sidebarKey) : null;
    } catch {
      /* Private storage can be unavailable. */
    }
    setNavCollapsed(saved === null ? preferences.sidebarCollapsed : saved === "1");
  }, [preferences, sidebarKey]);
  useEffect(
    () => () => {
      delete document.documentElement.dataset.appearance;
      delete document.documentElement.dataset.density;
      delete document.documentElement.dataset.reducedMotion;
    },
    [],
  );
  useEffect(() => {
    if (!account.data || initialLandingApplied.current) return;
    initialLandingApplied.current = true;
    const landing = account.data.preferences.landingPage;
    if (
      location.pathname === "/" &&
      landing !== "/" &&
      allowedLandingPages(account.data.role).some(([path]) => path === landing)
    )
      void navigate({ to: landing, replace: true });
  }, [account.data, location.pathname, navigate]);
  const { state: schedulingState } = useDemoScheduling();
  const encounterRouteActive = location.pathname === "/encounter" && selectedEncounter !== null;
  const visibleNavigation =
    schedulingState?.configuration.mode === "walk_in_only"
      ? navigation.filter((item) => item.to !== "/schedule")
      : navigation;

  // Sidebar sections own their child tabs. Changing sections hides those
  // tabs without unmounting their patient/encounter workspaces.
  const currentSection = sectionForPath(location.pathname);
  const {
    tabs: signedNoteTabs,
    openSignedNote,
    closeSignedNote,
    clearSignedNotes,
  } = useSignedNoteTabs();
  // Back stays within navigation performed in this session. The router retains
  // profile subsections and scroll positions, including across section changes.
  const [historyFloor, setHistoryFloor] = useState(location.state.__TSR_index);
  const canGoBack = location.state.__TSR_index > historyFloor;
  const workspace = useDemoWorkspace();
  const encounterTabLabel = selectedEncounter
    ? (selectedEncounter.label ??
      workspace.patientFor(selectedEncounter.patientId)?.displayName ??
      "Encounter")
    : null;
  const closeEncounterTab = (encounterId: string) => {
    if (!closeSelectionTab(encounterId)) return;
    if (location.pathname === "/encounter" && selectedEncounter?.encounterId === encounterId) {
      const next = encounterTabs.filter((tab) => tab.encounterId !== encounterId).at(-1);
      if (next) void navigate({ to: "/encounter", search: { tab: next.tabId }, replace: true });
      else void navigate({ to: "/" });
    }
  };
  const returnToMicrophone = () => {
    cancelEncounterSwitch();
    const source = encounterTabs.find((tab) => tab.encounterId === microphone?.encounterId);
    if (source && selectEncounter(source)) {
      void navigate({ to: "/encounter", search: { tab: source.tabId } });
    } else if (microphone?.patientId) {
      void navigate({
        to: "/patients/$patientId",
        params: { patientId: microphone.patientId },
        search: { section: "overview" },
      });
    }
  };

  // Each patient gets a named profile tab and an independent keep-alive pane.
  // The route selects the active profile without replacing the other patients.
  const {
    tabs: profileTabs,
    closeProfileTab,
    clearProfileTabs,
    setProfileTabLabel,
    setProfileTabSection,
  } = usePatientProfileTab();
  const closeProfileTabAndLeave = (patientId: string) => {
    if (location.pathname === `/patients/${patientId}`) {
      void navigate({ to: "/patients" });
    }
    closeProfileTab(patientId);
  };

  const renderProfileWorkspaceTab = (profileTab: PatientProfileTab) => {
    const profileActive = location.pathname === `/patients/${profileTab.patientId}`;
    const profileTabLabel = profileTab.label ?? "Patient profile";
    return (
      <div className={`ib-shell-tab ${profileActive ? "active" : ""}`} key={profileTab.patientId}>
        <button
          type="button"
          className="ib-shell-tab-btn"
          aria-current={profileActive ? "page" : undefined}
          onClick={() =>
            void navigate({
              to: "/patients/$patientId",
              params: { patientId: profileTab.patientId },
              search: { section: profileTab.section },
            })
          }
        >
          <Icon name="person" />
          <span className="ib-shell-tab-label">{profileTabLabel}</span>
        </button>
        <button
          type="button"
          className="ib-shell-tab-close"
          aria-label={`Close profile tab — ${profileTabLabel}`}
          onClick={() => closeProfileTabAndLeave(profileTab.patientId)}
        >
          <Icon name="close" />
        </button>
      </div>
    );
  };

  const endSession = async () => {
    if (
      accountDirty &&
      !window.confirm("Log out and discard your unfinished profile or signature changes?")
    )
      return;
    const persisted = runtime.mode === "supabase";
    // Note changes still waiting for a pause in typing go to the server while the session can.
    // A visit already closed with changes the server never got is still in this browser alone.
    if (
      persisted &&
      (!(await flushNoteDrafts(NOTE_DRAFT_SIGN_OUT_FLUSH_MS)) || hasUnsavedNoteDrafts()) &&
      !window.confirm(
        "Some note changes could not be saved to the server yet. Log out anyway? Those changes will be lost.",
      )
    )
      return;
    try {
      await runtime.workforce.clear();
    } finally {
      clearTodayBoardView(runtime.workforce);
      clearClinicalSession(queryClient, clearEncounterSelection);
      // The server holds the persisted record; nothing of it stays in this browser for the next
      // person at this computer.
      if (persisted) {
        workspace.clearLocalWorkspace();
        clearNoteDraftSync();
      }
      clearProfileTabs();
      clearSignedNotes();
      await navigate({ to: "/", replace: true, ignoreBlocker: true });
      setHistoryFloor(router.history.location.state.__TSR_index);
    }
  };

  const toggleNavCollapsed = useCallback(() => {
    setNavCollapsed((current) => {
      const next = !current;
      try {
        if (preferences?.rememberSidebar && sidebarKey)
          localStorage.setItem(sidebarKey, next ? "1" : "0");
      } catch {
        /* Keep working without browser storage. */
      }
      return next;
    });
  }, [preferences?.rememberSidebar, sidebarKey]);

  const openCommandPalette = useCallback(() => setPaletteOpen(true), []);

  const horizonShellMenu = useMemo<HorizonShellMenu>(
    () => ({
      expanded: !navCollapsed,
      // The encounter header controls the same persistent sidebar as its
      // own expand/collapse button; it never opens a separate modal menu.
      open: toggleNavCollapsed,
    }),
    [navCollapsed, toggleNavCollapsed],
  );

  useEffect(() => {
    const openPalette = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openCommandPalette();
      }
    };
    window.addEventListener("keydown", openPalette);
    return () => window.removeEventListener("keydown", openPalette);
  }, [openCommandPalette]);

  return (
    <AccountWorkspaceActionsContext.Provider
      value={{
        signOut: endSession,
        setAccountDirty,
        clinicSwitchBlocker: microphone
          ? "Finish recording before switching clinics."
          : encounterTabs.length || profileTabs.length || signedNoteTabs.length
            ? "Close your patient and encounter tabs before switching clinics. Your open work stays available until then."
            : null,
        switchClinic: async (siteId) => {
          if (microphone || encounterTabs.length || profileTabs.length || signedNoteTabs.length)
            throw new Error(
              "Close your patient tabs and finish recording before switching clinics.",
            );
          if (!runtime.switchSite)
            throw new Error("Clinic switching is unavailable in this session.");
          await runtime.switchSite(siteId, async () => {
            await queryClient.cancelQueries();
            clearTodayBoardView(runtime.workforce);
            clearClinicalSession(queryClient, clearEncounterSelection);
            clearProfileTabs();
            clearSignedNotes();
            await navigate({ to: "/", replace: true });
          });
        },
      }}
    >
      <div
        className={
          navCollapsed
            ? "app-shell horizon-chrome soft-glass-shell nav-collapsed"
            : "app-shell horizon-chrome soft-glass-shell"
        }
      >
        <aside id="horizon-clinical-menu" className="sidebar" aria-label="Clinical workspace">
          <Link to="/" className="brand bob-brand" aria-label="Bob Doc clinical home">
            <Bob variant={navCollapsed ? "symbol" : "wordmark"} decorative />
          </Link>
          <button
            type="button"
            className="global-search"
            aria-label="Search or command"
            aria-haspopup="dialog"
            aria-expanded={paletteOpen}
            onClick={openCommandPalette}
          >
            <Icon name="search" />
            <span>Search</span>
            <kbd>⌘ K</kbd>
          </button>
          <nav aria-label="Main navigation">
            {visibleNavigation.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className={`nav-link${currentSection === item.to ? " active" : ""}${item.to === "/billing" ? " nav-link-secondary-start" : ""}${item.to === "/admin" ? " nav-link-admin" : ""}`}
                activeProps={{}}
                aria-current={currentSection === item.to ? "page" : undefined}
                activeOptions={{ exact: item.to === "/" }}
                aria-label={item.label}
                title={navCollapsed ? item.label : undefined}
              >
                <Icon name={item.icon} />
                <span>{item.label}</span>
                {item.to === "/attention" ? (
                  <AttentionBadge runtime={runtime} />
                ) : item.badge ? (
                  <span className="nav-badge">
                    <span aria-hidden="true">{item.badge}</span>
                    <span className="sr-only">{item.badge} items</span>
                  </span>
                ) : null}
              </Link>
            ))}
            <Link
              to="/account"
              className="nav-link mobile-profile-link"
              activeProps={{ className: "nav-link mobile-profile-link active" }}
              aria-label="Profile settings"
            >
              <Icon name="person" />
              <span>Profile</span>
            </Link>
          </nav>
          <div className="sidebar-spacer" />
          <section className="clinic-status" aria-label="Clinic status">
            <span className="status-dot" aria-hidden="true" />
            <div>
              <strong>{workforceProfile?.organizationName ?? "Clinic"}</strong>
              <small>All systems ready</small>
            </div>
          </section>
          <button
            type="button"
            className="sidebar-collapse-toggle"
            aria-label={navCollapsed ? "Expand navigation" : "Collapse navigation"}
            aria-expanded={!navCollapsed}
            aria-controls="horizon-clinical-menu"
            onClick={toggleNavCollapsed}
          >
            <Icon name="chevron" />
            <span className="sidebar-collapse-label">Collapse menu</span>
          </button>
          <div className="user-menu">
            <Link className="user-card" to="/account" aria-label="Open my profile">
              {account.data?.photoDataUrl ? (
                <img className="avatar" src={account.data.photoDataUrl} alt="" />
              ) : (
                <span className="avatar avatar-sage" aria-hidden="true">
                  {workforceInitials(
                    account.data?.displayName ?? workforceProfile?.displayName ?? "",
                  )}
                </span>
              )}
              <span>
                <strong>
                  {account.data?.displayName ?? workforceProfile?.displayName ?? "My profile"}
                </strong>
                <small>
                  {account.data
                    ? `${workforceRoleLabel(account.data.role)} · ${account.data.siteName}`
                    : workforceProfile
                      ? workforceRoleLabel(workforceProfile.role)
                      : "Account settings"}
                </small>
              </span>
            </Link>
            <SidebarAccountMenu onLogout={endSession} />
          </div>
        </aside>
        <div className="main-column">
          <div className="ib-workspace-toolbar">
            <button
              type="button"
              className="ib-workspace-back"
              aria-label="Back"
              title="Go back"
              disabled={!canGoBack}
              onClick={() => router.history.back()}
            >
              <Icon name="arrow" />
            </button>
            <nav className="ib-shell-tabs" aria-label="Open workspaces">
              {currentSection === "/patients" ? profileTabs.map(renderProfileWorkspaceTab) : null}
              {currentSection === "/"
                ? encounterTabs.map((tab) => {
                    const active =
                      encounterRouteActive && selectedEncounter?.encounterId === tab.encounterId;
                    const label =
                      tab.label ?? workspace.patientFor(tab.patientId)?.displayName ?? "Encounter";
                    return (
                      <div
                        className={`ib-shell-tab ${active ? "active" : ""}`}
                        key={tab.encounterId}
                      >
                        <button
                          type="button"
                          className="ib-shell-tab-btn"
                          aria-current={active ? "page" : undefined}
                          onClick={() => {
                            if (selectEncounter(tab))
                              void navigate({ to: "/encounter", search: { tab: tab.tabId } });
                          }}
                        >
                          <span className="ib-shell-tab-dot" aria-hidden="true" />
                          <span className="ib-shell-tab-label">{label}</span>
                        </button>
                        <button
                          type="button"
                          className="ib-shell-tab-close"
                          aria-label={`Close encounter tab — ${label}`}
                          onClick={() => closeEncounterTab(tab.encounterId)}
                        >
                          <Icon name="close" />
                        </button>
                      </div>
                    );
                  })
                : null}
              {currentSection === "/"
                ? signedNoteTabs.map((tab) => {
                    const active = location.pathname === `/visit-note/${tab.id}`;
                    const date = tab.note.encounterStartedAt ?? tab.note.signedAt;
                    const label = `${tab.label} · Note${date ? ` · ${visitDateLabel(date)}` : ""}`;
                    return (
                      <div className={`ib-shell-tab ${active ? "active" : ""}`} key={tab.id}>
                        <button
                          type="button"
                          className="ib-shell-tab-btn"
                          aria-current={active ? "page" : undefined}
                          title={label}
                          onClick={() =>
                            void navigate({ to: "/visit-note/$tabId", params: { tabId: tab.id } })
                          }
                        >
                          <Icon name="lock" />
                          <span className="ib-shell-tab-label">{label}</span>
                        </button>
                        <button
                          type="button"
                          className="ib-shell-tab-close"
                          aria-label={`Close signed note tab — ${label}`}
                          onClick={() => {
                            closeSignedNote(tab.id);
                            if (active) void navigate({ to: "/" });
                          }}
                        >
                          <Icon name="close" />
                        </button>
                      </div>
                    );
                  })
                : null}
            </nav>
          </div>
          {microphone ? (
            <div className="ib-microphone-owner" role="status">
              <span>
                Microphone session · {microphone.label ?? "Current patient"}. Stop recording and
                wait for processing before switching encounters.
              </span>
              <button type="button" onClick={returnToMicrophone}>
                Return to recording
              </button>
            </div>
          ) : null}
          <HorizonShellMenuContext.Provider value={horizonShellMenu}>
            {encounterTabs.map((tab) => {
              const active =
                encounterRouteActive && selectedEncounter?.encounterId === tab.encounterId;
              return (
                <div
                  key={tab.encounterId}
                  className={active ? "ib-tab-pane" : "ib-tab-pane hidden"}
                  hidden={!active}
                  inert={!active}
                >
                  <RecordingScopeContext.Provider
                    value={{
                      encounterId: tab.encounterId,
                      patientId: tab.patientId,
                      label: tab.label ?? workspace.patientFor(tab.patientId)?.displayName,
                      active,
                    }}
                  >
                    <Suspense fallback={<RouteLoading label="Opening patient workspace" />}>
                      <PersistedHorizonEncounter
                        patientId={tab.patientId}
                        encounterId={tab.encounterId}
                        runtime={runtime}
                        hidden={!active}
                        handoutEveryVisit={handoutEveryVisit}
                      />
                    </Suspense>
                  </RecordingScopeContext.Provider>
                </div>
              );
            })}
            {profileTabs.map((profileTab) => {
              const profileActive = location.pathname === `/patients/${profileTab.patientId}`;
              return (
                // The keyed outer pane keeps each patient's editor and loaded
                // chart alive through tab switches and changes to tab order.
                <div
                  key={profileTab.patientId}
                  className={
                    profileActive
                      ? "ib-tab-pane ib-profile-pane"
                      : "ib-tab-pane ib-profile-pane hidden"
                  }
                >
                  <RecordingScopeContext.Provider
                    value={{
                      patientId: profileTab.patientId,
                      label: profileTab.label ?? undefined,
                      active: profileActive,
                    }}
                  >
                    <Suspense fallback={<RouteLoading label="Opening the patient profile" />}>
                      <PatientProfilePage
                        active={profileActive}
                        patientId={profileTab.patientId}
                        section={profileTab.section}
                        onSectionChange={(next) => {
                          setProfileTabSection(profileTab.patientId, next);
                          void navigate({
                            to: "/patients/$patientId",
                            params: { patientId: profileTab.patientId },
                            search: { section: next },
                            replace: true,
                          });
                        }}
                        onDisplayName={(name) => setProfileTabLabel(profileTab.patientId, name)}
                        onViewNote={(note, label) => {
                          const tabId = openSignedNote({
                            patientId: profileTab.patientId,
                            label,
                            note,
                          });
                          void navigate({ to: "/visit-note/$tabId", params: { tabId } });
                        }}
                        runtime={runtime}
                      />
                    </Suspense>
                  </RecordingScopeContext.Provider>
                </div>
              );
            })}
            <Outlet />
            {runtime.mode === "supabase" && <IncomingDocumentAlerts runtime={runtime} />}
          </HorizonShellMenuContext.Provider>
        </div>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        {pendingEncounterSwitch ? (
          // Keep navigation here while the provider guards synchronous selection.
          <EncounterSwitchConfirm
            currentName={microphone?.label ?? encounterTabLabel}
            busy={microphoneBusy}
            onKeepCurrent={returnToMicrophone}
            onOpenNew={() => {
              const next = confirmEncounterSwitch();
              if (next !== null) void navigate({ to: "/encounter", search: {} });
              else if (!microphoneBusy) void navigate({ to: "/" });
            }}
          />
        ) : null}
      </div>
    </AccountWorkspaceActionsContext.Provider>
  );
}
